import React, { useState, useEffect, useCallback } from 'react';
import { documentsApi, hotelsApi, roomTypesApi, reservationsApi, guestsApi, paymentsApi, billSettingsApi, invoicesApi } from '../../services/api';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { fmt } from '../../utils/billing';
import { useNotify } from '../../context/ConfirmContext';

const TABS = [
  { id: 'proofs', label: '🪪 Guest ID Proofs' },
  { id: 'invoices', label: '🧾 Invoices' },
  { id: 'expenses', label: '💼 Expense Receipts' },
];

// Documents oversight — superadmin-only cross-hotel browsing of the three
// document types stored as base64 blobs elsewhere in the app (guest ID scans,
// invoice PDFs, expense receipts). List calls never fetch the blob itself
// (see routes/oversight.js) — only "View" lazy-loads the actual file for one row.
export default function DocumentsPage() {
  const notify = useNotify();
  const [tab, setTab] = useState('proofs');
  const [hotels, setHotels] = useState([]);
  const [hotelFilter, setHotelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [roomTypeFilter, setRoomTypeFilter] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null); // { title, url }
  const [viewerLoading, setViewerLoading] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);

  useEffect(() => { hotelsApi.list().then(setHotels).catch(() => {}); }, []);

  // Room type names aren't unique across hotels, so filtering by room type
  // only makes sense once a specific hotel is picked — otherwise "Deluxe" from
  // one property would silently match "Deluxe" from another with a different
  // price/config. Not applicable to Expense Receipts at all (see backend note).
  useEffect(() => {
    setRoomTypeFilter('');
    if (hotelFilter) roomTypesApi.list(hotelFilter).then(setRoomTypes).catch(() => setRoomTypes([]));
    else setRoomTypes([]);
  }, [hotelFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { hotelId: hotelFilter, search, startDate, endDate, roomType: roomTypeFilter };
      let data;
      if (tab === 'proofs') data = await documentsApi.listProofs(filters);
      else if (tab === 'invoices') data = await documentsApi.listInvoices(filters);
      else data = await documentsApi.listExpenses(filters);
      setRows(data || []);
    } catch (err) {
      console.error(err);
      setRows([]);
    }
    setLoading(false);
  }, [tab, hotelFilter, search, startDate, endDate, roomTypeFilter]);

  useEffect(() => { load(); }, [load]);

  const openViewer = async (row) => {
    setViewerLoading(true);
    setViewer({ title: '', url: null });
    try {
      if (tab === 'proofs') {
        const full = await documentsApi.getProof(row.id);
        setViewer({ title: `${row.guest_name || 'Guest'} — ${row.type}`, url: full.data });
      } else if (tab === 'invoices') {
        const full = await documentsApi.getInvoice(row.id);
        setViewer({ title: `Invoice ${row.invoice_number}`, url: full.pdf_url });
      } else {
        const full = await documentsApi.getExpense(row.id);
        setViewer({ title: row.description || 'Expense Receipt', url: full.bill_image_url });
      }
    } catch (err) {
      await notify(err.message);
      setViewer(null);
    }
    setViewerLoading(false);
  };

  // Some older/legacy invoices never had their PDF saved at checkout time (a
  // separate gap from this page). Rather than chase that down here, reuse the
  // same regenerate-from-reservation-data approach already relied on by the
  // "reprint" action on the Reservations page — rebuild the PDF from what's
  // still in the reservation record and save it back onto the invoice.
  const regenerateInvoicePdf = async (row) => {
    setRegeneratingId(row.id);
    try {
      if (!row.reservation_id) throw new Error('This invoice has no linked reservation to regenerate from.');
      const reservation = await reservationsApi.get(row.reservation_id);
      let guestData = {};
      const guestId = reservation.guest_id || reservation.guestId;
      if (guestId) { try { guestData = await guestsApi.get(guestId); } catch (_) {} }
      let paymentsList = [];
      try { paymentsList = await paymentsApi.forReservation(row.reservation_id); } catch (_) {}
      let billDesign = {};
      try { billDesign = await billSettingsApi.get(row.hotel_id); } catch (_) {}
      const hotelData = hotels.find(h => h.id === row.hotel_id) || {};

      const { pdfDataUrl } = await generateInvoicePDF({
        reservation,
        guestData: { ...reservation, ...guestData },
        hotelData,
        billDesign,
        paymentsList,
        invoiceNumber: row.invoice_number,
        billingType: reservation.billing_type || reservation.billingType,
      });

      await invoicesApi.update(row.id, { pdfUrl: pdfDataUrl });
      setViewer({ title: `Invoice ${row.invoice_number}`, url: pdfDataUrl });
      load();
    } catch (err) {
      await notify('Could not generate PDF: ' + err.message);
    }
    setRegeneratingId(null);
  };

  const hasActiveFilters = hotelFilter || startDate || endDate || roomTypeFilter || search;

  return (
    <div className="panel animate-in">
      <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span className="panel-title">📁 Documents</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`btn btn-sm ${tab === t.id ? '' : 'btn-ghost'}`}
              style={tab === t.id ? { background: 'var(--accent)', color: 'white' } : {}}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '12px 24px 0' }}>
        <select className="input" style={{ width: 170, padding: '4px 8px', fontSize: 13 }} value={hotelFilter} onChange={e => setHotelFilter(e.target.value)}>
          <option value="">All Hotels</option>
          {hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>From</span>
        <input type="date" className="input" style={{ width: 145, padding: '4px 8px', fontSize: 13 }} value={startDate} onChange={e => setStartDate(e.target.value)} />
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>To</span>
        <input type="date" className="input" style={{ width: 145, padding: '4px 8px', fontSize: 13 }} value={endDate} onChange={e => setEndDate(e.target.value)} />
        {tab !== 'expenses' && (
          <select
            className="input"
            style={{ width: 160, padding: '4px 8px', fontSize: 13 }}
            value={roomTypeFilter}
            onChange={e => setRoomTypeFilter(e.target.value)}
            disabled={!hotelFilter}
            title={!hotelFilter ? 'Pick a hotel first — room type names aren’t unique across hotels' : ''}
          >
            <option value="">{hotelFilter ? 'All Room Types' : 'Room Type (pick hotel)'}</option>
            {roomTypes.map(rt => <option key={rt.id} value={rt.name}>{rt.name}</option>)}
          </select>
        )}
        <input
          className="input"
          style={{ width: 190, padding: '4px 8px', fontSize: 13 }}
          placeholder={tab === 'expenses' ? 'Search category/description...' : 'Search guest name...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {hasActiveFilters && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setHotelFilter(''); setStartDate(''); setEndDate(''); setRoomTypeFilter(''); setSearch(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div style={{ padding: 24 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0', color: 'var(--muted)' }}>
            <div className="spin" style={{ marginBottom: 16 }}>🌀</div>
            <div>Loading documents...</div>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '100px 0', background: 'var(--s1)', borderRadius: 12, color: 'var(--muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <div style={{ fontWeight: 700 }}>No documents found.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                {tab === 'proofs' && (
                  <tr><th>Guest</th><th>Hotel</th><th>ID Type</th><th>File</th><th>Uploaded</th><th></th></tr>
                )}
                {tab === 'invoices' && (
                  <tr><th>Invoice #</th><th>Guest</th><th>Hotel</th><th style={{ textAlign: 'right' }}>Amount</th><th>Type</th><th>Date</th><th></th></tr>
                )}
                {tab === 'expenses' && (
                  <tr><th>Date</th><th>Hotel</th><th>Category</th><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th><th>Receipt</th><th></th></tr>
                )}
              </thead>
              <tbody>
                {tab === 'proofs' && rows.map(r => (
                  <tr key={r.id}>
                    <td>{r.guest_name || '—'}</td>
                    <td>{r.hotel_name || '—'}</td>
                    <td>{r.type}</td>
                    <td>{r.file_name}</td>
                    <td>{new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => openViewer(r)}>View</button></td>
                  </tr>
                ))}
                {tab === 'invoices' && rows.map(r => (
                  <tr key={r.id}>
                    <td>{r.invoice_number}</td>
                    <td>{r.guest_name || '—'}</td>
                    <td>{r.hotel_name || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.total_amount)}</td>
                    <td>{r.billing_type === 'gst' ? 'GST' : 'Non-GST'}</td>
                    <td>{new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td>
                      {r.has_pdf ? (
                        <button className="btn btn-ghost btn-sm" onClick={() => openViewer(r)}>View</button>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          title="No saved copy — rebuild it from the reservation record"
                          disabled={regeneratingId === r.id}
                          onClick={() => regenerateInvoicePdf(r)}
                        >
                          {regeneratingId === r.id ? 'Generating…' : 'Generate PDF'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {tab === 'expenses' && rows.map(r => (
                  <tr key={r.id}>
                    <td>{new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td>{r.hotel_name || '—'}</td>
                    <td>{r.category}</td>
                    <td>{r.description || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
                    <td>{r.has_receipt ? '✅ Attached' : '—'}</td>
                    <td>{r.has_receipt ? <button className="btn btn-ghost btn-sm" onClick={() => openViewer(r)}>View</button> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewer && (
        <div className="overlay" onClick={() => setViewer(null)} style={{ zIndex: 9999 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 650, padding: 15, background: 'var(--bg)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
              <div className="modal-title" style={{ marginBottom: 0 }}>{viewer.title || 'Document'}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewer(null)}>✕ Close</button>
            </div>
            <div style={{ background: '#000', borderRadius: 8, padding: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              {viewerLoading || !viewer.url ? (
                <div style={{ color: '#fff' }}>{viewerLoading ? 'Loading…' : 'No file available.'}</div>
              ) : viewer.url.startsWith('data:application/pdf') ? (
                <iframe src={viewer.url} title={viewer.title} width="100%" height="500px" style={{ border: 'none', background: '#fff' }} />
              ) : (
                <img src={viewer.url} alt={viewer.title} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
